import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Helmet from 'helmet/helmet';
import { Route, Redirect } from 'react-router';

import LoadingSwitch from 'lib/react-router/loading-switch';
import RouteWithSearch from 'lib/react-router/route-with-search';
import RedirectQueryParam from 'lib/react-router/redirect-query-param';
import Localizable from 'lib/intl/localizable';
import { SectionCountConsumer } from 'lib/section-count-context/section-count-context';
import ScrollToTop from 'lib/scroll/scroll-to-top';

import NotFound from 'four-nets/errors/404/not-found';
import { CategoryStore } from 'four-nets/forum/forum';
import SubNavigation from 'four-nets/ui-v2/sub-navigation/sub-navigation';

import WhereIPostedLoader from './where-i-posted/where-i-posted-loader';
import SubscribedTopicsLoader from './subscribed-topics/subscribed-topics-loader';
import UpdatedTopicsLoader from './updated-topics/updated-topics-loader';
import LatestTopicsLoader from './latest-topics/latest-topics-loader';

import CategoriesLoader from './categories/categories-loader';
import CategoryLoader from './category/category-loader';
import ThreadLoader from './thread/thread-loader';

@Localizable()
class ForumView extends React.PureComponent {
    static contextTypes = {
        site: PropTypes.object,
    };

    static childContextTypes = {
        categoryStore: PropTypes.instanceOf(CategoryStore),
    };

    getChildContext() {
        return {
            categoryStore: this.props.categoryStore,
        };
    }

    UNSAFE_componentWillMount() {
        const { site } = this.context;

        this.links = [
            {
                label: <FormattedMessage id='FORUM_CATEGORIES' />,
                to: '/forum/',
            },
            {
                label: <FormattedMessage id='FORUM_LATEST_DISCUSSIONS' />,
                to: '/forum/latest-topics/',
            },
            {
                label: <FormattedMessage id='FORUM_WHERE_IS_DISCUSSED' />,
                to: '/forum/updated-topics/',
            },
            {
                label: <FormattedMessage id='FORUM_WHERE_I_POSTED' values={{ gender: 'm' }} />,
                to: '/forum/where-i-have-posted/',
            },
            {
                label: <FormattedMessage id='SUBSCRIBED_THREADS' />,
                to: '/forum/subscribed-topics/',
            },
        ];

        if (!site.isGarden) {
            this.links.push({
                label: <FormattedMessage id='FORUM_PRIVATE_DISCUSSIONS' />,
                to: '/forum/private-discussions/',
            });
        }
    }

    renderHelmet = H => {
        const { site } = this.context;

        if (site.isGarden) {
            return (
                <FormattedMessage id='PAGE_TITLE_INDEX_ALL_ON_GARDEN'>
                    {title => (
                        <FormattedMessage id='FORUM_THREAD_INDEX_DESCRIPTION_GARDEN'>
                            {desc => (
                                <H>
                                    <title>
                                        {title} | {site.name}
                                    </title>
                                    <meta property='og:title' content={`${title} | ${site.name}`} />
                                    <meta name='Description' content={desc} />
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else if (site.isLiving) {
            return (
                <FormattedMessage id='PAGE_TITLE_INDEX_ALL_ON_LIVING'>
                    {title => (
                        <FormattedMessage id='FORUM_THREAD_INDEX_DESCRIPTION_LIVING'>
                            {desc => (
                                <H>
                                    <title>
                                        {title} | {site.name}
                                    </title>
                                    <meta property='og:title' content={`${title} | ${site.name}`} />
                                    <meta name='Description' content={desc} />
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        }
        return (
            <FormattedMessage id='PAGE_TITLE_INDEX_ALL_ON_WEDDING'>
                {title => (
                    <FormattedMessage id='FORUM_THREAD_INDEX_DESCRIPTION_WEDDING'>
                        {desc => (
                            <H>
                                <title>
                                    {title} | {site.name}
                                </title>
                                <meta property='og:title' content={`${title} | ${site.name}`} />
                                <meta name='Description' content={desc} />
                            </H>
                        )}
                    </FormattedMessage>
                )}
            </FormattedMessage>
        );
    };

    render() {
        const { links } = this;
        const { site } = this.context;

        return (
            <React.Fragment>
                <SectionCountConsumer>
                    {({ sectionCounts }) => <ScrollToTop scrollOnMount={false} k={sectionCounts.SECTION_FORUM} />}
                </SectionCountConsumer>
                <Helmet render={this.renderHelmet} />
                <SubNavigation links={links} forum={true} />
                <LoadingSwitch>
                    <Route exact path='/forum/' component={CategoriesLoader} />
                    <Redirect exact from='/diskuse/' to='/forum/' />
                    <RedirectQueryParam
                        param='type'
                        exact
                        path='/forum/where-i-have-posted'
                        component={WhereIPostedLoader}
                    />
                    <Redirect exact from='/diskuse/where-i-have-posted' to='/forum/where-i-have-posted' />
                    <RedirectQueryParam
                        param='type'
                        exact
                        path='/forum/subscribed-topics'
                        component={SubscribedTopicsLoader}
                    />
                    <Redirect exact from='/diskuse/subscribed-topics' to='/forum/subscribed-topics' />
                    <RedirectQueryParam
                        param='type'
                        exact
                        path='/forum/updated-topics'
                        component={UpdatedTopicsLoader}
                    />
                    <Redirect exact from='/diskuse/updated-topics' to='/forum/updated-topics' />
                    <RedirectQueryParam param='type' exact path='/forum/latest-topics' component={LatestTopicsLoader} />
                    <Redirect exact from='/diskuse/latest-topics' to='/forum/latest-topics' />
                    <Route
                        exact
                        path='/forum/private-discussions'
                        render={props => (
                            <CategoryLoader
                                {...props}
                                match={{ params: { slug: site.isSlovak ? 'sukromne-diskusie' : 'soukrome-diskuze' } }}
                            />
                        )}
                    />
                    <Redirect exact from='/diskuse/private-discussions' to='/forum/private-discussions' />
                    <RedirectQueryParam param='type' exact path='/forum/category/:slug' component={CategoryLoader} />
                    <Redirect exact from='/diskuse/category/:slug' to='/forum/category/:slug' />
                    <RouteWithSearch exact path='/forum/:category/:slug' component={ThreadLoader} />
                    <Redirect exact from='/diskuse/:category/:slug' to='/forum/:category/:slug' />
                    <Redirect exact from='/forum/soukrome-diskuze/:slug' to='/forum/private-discussions/:slug' />
                    <Redirect exact from='/forum/sukromne-diskusie/:slug' to='/forum/private-discussions/:slug' />
                    <Route component={NotFound} />
                </LoadingSwitch>
            </React.Fragment>
        );
    }
}

export default ForumView;
